import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext.jsx'
import api from '../../lib/api.jsx'
import { Eye, EyeOff, ArrowRight, AlertCircle, UserPlus, LogIn } from 'lucide-react'
import './Login.css'

function AcceptInvite() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { isAuthenticated, isLoading, login } = useAuth()

  const [mode, setMode] = useState('login') // 'login' or 'signup'
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [acceptingInvite, setAcceptingInvite] = useState(false)

  // If already logged in, accept the invite directly via API
  useEffect(() => {
    if (!isLoading && isAuthenticated && token && !acceptingInvite) {
      setAcceptingInvite(true)
      api.get(`/workspace/accept-invite?token=${token}`)
        .then(() => {
          navigate('/settings', { replace: true })
        })
        .catch(err => {
          const detail = err.response?.data?.detail || 'Failed to accept invite'
          setError(detail)
          setAcceptingInvite(false)
        })
    }
  }, [isLoading, isAuthenticated, token, navigate, acceptingInvite])

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-bg"><div className="bg-gradient"></div><div className="bg-grid"></div></div>
        <div className="login-container" style={{ maxWidth: 480, justifyContent: 'center' }}>
          <div className="login-form-section" style={{ flex: 'none', width: '100%' }}>
            <div className="login-form-container" style={{ maxWidth: 360 }}>
              <div className="error-message"><AlertCircle size={18} /><span>Invalid invite link. No token provided.</span></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading || acceptingInvite) {
    return (
      <div className="login-page">
        <div className="login-bg"><div className="bg-gradient"></div><div className="bg-grid"></div></div>
        <div className="login-container" style={{ maxWidth: 480, justifyContent: 'center' }}>
          <div className="login-form-section" style={{ flex: 'none', width: '100%', textAlign: 'center' }}>
            <div className="login-form-container" style={{ maxWidth: 360 }}>
              <div className="login-header"><h2>Accepting Invite...</h2><p>Please wait while we set up your workspace access.</p></div>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                <span className="btn-loading" style={{ width: 32, height: 32, borderWidth: 3, borderColor: 'rgba(99,102,241,0.2)', borderTopColor: 'var(--color-primary)' }}></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Not logged in — show login/signup form
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')
    try {
      const result = await login(formData.email, formData.password, true)
      if (!result.success) {
        setError(result.error || 'Invalid email or password')
        setIsSubmitting(false)
        return
      }
      // After login, the useEffect will auto-accept the invite
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password')
      setIsSubmitting(false)
    }
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setIsSubmitting(false)
      return
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      setIsSubmitting(false)
      return
    }

    try {
      // Register with invite token — backend auto-accepts the invite
      const res = await api.post(`/auth/register?invite=${token}`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      })
      const accessToken = res.data.access_token
      if (accessToken) {
        localStorage.setItem('access_token', accessToken)
        // Fetch user profile
        const meRes = await api.get('/auth/me', { headers: { Authorization: `Bearer ${accessToken}` } })
        const me = meRes.data
        const userData = {
          id: me.id, email: me.email, name: me.name || formData.name,
          role: me.role || 'user', workspace_id: me.workspace_id || null,
          workspace_role: me.workspace_role || 'Editor', workspace_name: me.workspace_name || null,
          avatar: me.avatar || null, company: me.company || null,
          timezone: me.timezone || 'America/New_York', language: me.language || 'en',
        }
        localStorage.setItem('synxreach_user', JSON.stringify(userData))
        navigate('/dashboard', { replace: true })
        window.location.reload()
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-bg"><div className="bg-gradient"></div><div className="bg-grid"></div></div>
      <div className="login-container">
        <div className="login-branding">
          <div className="branding-content">
            <div className="branding-logo"><img src="/logo_black.png" alt="Synxreach" className="branding-logo-img" /></div>
            <h1 className="branding-headline">You've Been Invited!</h1>
            <p className="branding-subtitle">Someone invited you to collaborate on SynxReach. Sign in or create an account to join the workspace.</p>
            <div className="branding-features">
              <div className="feature"><span className="feature-icon">Team</span><span>Collaborate with your team</span></div>
              <div className="feature"><span className="feature-icon">AI</span><span>AI-Powered Email Outreach</span></div>
              <div className="feature"><span className="feature-icon">Live</span><span>Real-time Analytics</span></div>
            </div>
          </div>
        </div>
        <div className="login-form-section">
          <div className="login-form-container">
            {/* Toggle between Login and Signup */}
            <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', background: 'var(--color-bg-secondary)', borderRadius: 10, padding: 4 }}>
              <button type="button" onClick={() => { setMode('login'); setError('') }}
                style={{
                  flex: 1, padding: '10px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: mode === 'login' ? '#fff' : 'transparent',
                  color: mode === 'login' ? '#6366f1' : '#64748b',
                  boxShadow: mode === 'login' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                }}>
                <LogIn size={15} /> Sign In
              </button>
              <button type="button" onClick={() => { setMode('signup'); setError('') }}
                style={{
                  flex: 1, padding: '10px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: mode === 'signup' ? '#fff' : 'transparent',
                  color: mode === 'signup' ? '#6366f1' : '#64748b',
                  boxShadow: mode === 'signup' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                }}>
                <UserPlus size={15} /> Create Account
              </button>
            </div>

            <div className="login-header">
              <h2>{mode === 'login' ? 'Welcome back' : 'Create your account'}</h2>
              <p>{mode === 'login' ? 'Sign in to accept the workspace invite' : 'Sign up to join the workspace'}</p>
            </div>

            <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="login-form">
              {error && <div className="error-message"><AlertCircle size={18} /><span>{error}</span></div>}

              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="name">Full name</label>
                  <div className="input-wrapper">
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                      placeholder="John Doe" required autoComplete="name" />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <div className="input-wrapper">
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                    placeholder="you@company.com" required autoComplete="email" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange}
                    placeholder={mode === 'login' ? 'Enter your password' : 'Create a password (min 6 chars)'}
                    required autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm password</label>
                  <div className="input-wrapper">
                    <input type="password" id="confirmPassword" name="confirmPassword"
                      value={formData.confirmPassword} onChange={handleChange}
                      placeholder="Confirm your password" required autoComplete="new-password" />
                  </div>
                </div>
              )}

              <button type="submit" className="login-btn" disabled={isSubmitting}>
                {isSubmitting
                  ? <span className="btn-loading"></span>
                  : <><span>{mode === 'login' ? 'Sign in & Join Workspace' : 'Create Account & Join'}</span><ArrowRight size={18} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AcceptInvite
